import { AxiosError, InternalAxiosRequestConfig } from "axios";

import { Api } from "./generated/api-client";
import { getBaseUrl } from "./base-url";
import { getActivePreviewMemberId, previewSignalOn } from "./preview-as";
import {
  endSessionFromUnauthorized,
  shouldClearSessionOn401,
} from "./unauthorized-session";

function requestAuthorization(
  config: InternalAxiosRequestConfig | undefined,
): string | null {
  if (!config?.headers) return null;
  const headers = config.headers as {
    Authorization?: unknown;
    authorization?: unknown;
    get?: (name: string) => unknown;
  };
  const value =
    (typeof headers.get === "function"
      ? headers.get("Authorization") ?? headers.get("authorization")
      : undefined) ??
    headers.Authorization ??
    headers.authorization;
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

export const getApi = ({ token }: { token?: string | null } = {}) => {
  const api = new Api({
    baseURL: getBaseUrl(),
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  api.instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const memberId = getActivePreviewMemberId();
    if (!memberId) return config;
    const url = config.url ?? "";
    if (!previewSignalOn(config.method ?? "GET", url)) return config;
    const params = {
      ...(config.params as Record<string, unknown> | undefined),
    };
    if (params.previewAs == null || params.previewAs === "") {
      params.previewAs = memberId;
    }
    config.params = params;
    return config;
  });
  api.instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (
        shouldClearSessionOn401({
          status: error.response?.status,
          url: error.config?.url,
          method: error.config?.method,
          authorization: requestAuthorization(error.config),
        })
      ) {
        endSessionFromUnauthorized();
      }
      return Promise.reject(error);
    },
  );
  return api;
};
